import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { PricingCalculator, PRICING } from '../components/PricingCalculator';
import { tutorAPI, subjectsAPI, publicContentAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve tutor profile photo URLs (handles relative /api/... paths)
const getTutorPhotoUrl = (photoUrl) => {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl;
  if (photoUrl.startsWith('/')) return `${API_URL}${photoUrl}`;
  return photoUrl;
};

import { 
  GraduationCap, 
  ArrowRight,
  ArrowLeft,
  Star,
  Users,
  Clock,
  Shield,
  Menu,
  X,
  BookOpen,
  CheckCircle,
  Calendar,
  MessageCircle,
  Loader2,
  CreditCard,
  UserCheck,
  FileText,
  Headphones
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: "easeOut" }
};

// Navbar component
const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'tutor') return '/tutor-dashboard';
    return '/dashboard';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-indigo-700" />
            <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">Gifted Academy Tutors</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/tutors" className="text-slate-600 hover:text-slate-900 transition-colors">
              Find Tutors
            </Link>
            <Link to="/pricing" className="text-indigo-700 font-medium">
              Pricing
            </Link>
            <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors">
              About
            </Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">
              Contact
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to={getDashboardLink()}>
                  <Button variant="ghost" className="rounded-full" data-testid="dashboard-btn">
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="rounded-full" 
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="get-started-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-100">
            <div className="flex flex-col gap-4">
              <Link to="/tutors" className="text-slate-600 py-2">Find Tutors</Link>
              <Link to="/pricing" className="text-indigo-700 font-medium py-2">Pricing</Link>
              <Link to="/about" className="text-slate-600 py-2">About</Link>
              <Link to="/contact" className="text-slate-600 py-2">Contact</Link>
              {isAuthenticated ? (
                <>
                  <Link to={getDashboardLink()} className="text-slate-600 py-2">Dashboard</Link>
                  <Button variant="outline" onClick={logout}>Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login"><Button variant="outline" className="w-full">Sign In</Button></Link>
                  <Link to="/register"><Button className="w-full bg-slate-900">Get Started</Button></Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Step indicator
const StepIndicator = ({ currentStep }) => {
  const steps = [
    { num: 1, label: 'Select Package' },
    { num: 2, label: 'Choose Subjects' },
    { num: 3, label: 'View Tutors' }
  ];

  return (
    <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step.num}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
              currentStep >= step.num 
                ? 'bg-indigo-700 text-white' 
                : 'bg-stone-200 text-slate-500'
            }`}>
              {currentStep > step.num ? <CheckCircle className="h-5 w-5" /> : step.num}
            </div>
            <span className={`hidden sm:inline text-sm font-medium ${
              currentStep >= step.num ? 'text-slate-900' : 'text-slate-400'
            }`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 md:w-16 h-0.5 ${
              currentStep > step.num ? 'bg-indigo-700' : 'bg-stone-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Subject Selection Component
const SubjectSelection = ({ subjects, selectedSubjects, onToggle, gradeLevel }) => {
  const filteredSubjects = subjects;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filteredSubjects.map((subject) => {
          const isSelected = selectedSubjects.includes(subject);
          return (
            <div
              key={subject}
              onClick={() => onToggle(subject)}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                isSelected 
                  ? 'border-indigo-700 bg-indigo-50' 
                  : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
              }`}
              data-testid={`subject-${subject.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Checkbox 
                checked={isSelected} 
                className="pointer-events-none"
              />
              <span className={`text-sm font-medium ${isSelected ? 'text-indigo-700' : 'text-slate-700'}`}>
                {subject}
              </span>
            </div>
          );
        })}
      </div>
      {selectedSubjects.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-4 border-t border-stone-100">
          <span className="text-sm text-slate-500">Selected:</span>
          {selectedSubjects.map((subject) => (
            <span 
              key={subject}
              className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full"
            >
              {subject}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Tutor Card Component
const TutorCard = ({ tutor, onContact, isSelected, onSelect }) => {
  const photoUrl = getTutorPhotoUrl(tutor.profile_photo_url) || tutor.picture;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => onSelect && onSelect(tutor)}
      className={`bg-white rounded-2xl border-2 overflow-hidden hover:shadow-lg transition-all cursor-pointer ${
        isSelected 
          ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-lg' 
          : 'border-stone-200 hover:border-indigo-300'
      }`}
      data-testid={`tutor-card-${tutor.profile_id || tutor.user_id}`}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="bg-indigo-600 text-white text-xs font-medium py-1.5 px-4 text-center">
          <CheckCircle className="h-3 w-3 inline mr-1" />
          Selected as Preferred Tutor
        </div>
      )}
      
      <div className="p-6">
        {/* Header with photo and name */}
        <div className="flex items-start gap-4 mb-4">
          <Avatar className={`h-16 w-16 border-2 ${isSelected ? 'border-indigo-300' : 'border-stone-100'}`}>
            <AvatarImage src={photoUrl} alt={tutor.name} />
            <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xl font-semibold">
              {tutor.name?.charAt(0) || 'T'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-semibold text-slate-900 truncate">
              {tutor.name}
            </h3>
            {tutor.rating_average > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <span className="text-sm font-medium text-slate-700">
                  {tutor.rating_average.toFixed(1)}
                </span>
                <span className="text-sm text-slate-400">
                  ({tutor.total_reviews} reviews)
                </span>
              </div>
            )}
            {tutor.experience_years > 0 && (
              <p className="text-sm text-slate-500 mt-1">
                {tutor.experience_years} years experience
              </p>
            )}
          </div>
        </div>

        {/* Subjects taught */}
        <div className="mb-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
            Subjects
          </p>
          <div className="flex flex-wrap gap-1.5">
            {tutor.subjects?.slice(0, 5).map((subject) => (
              <span 
                key={subject}
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  tutor.matched_subjects?.includes(subject)
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-stone-100 text-slate-600'
                }`}
              >
                {subject}
              </span>
            ))}
            {tutor.subjects?.length > 5 && (
              <span className="px-2 py-1 text-xs text-slate-400">
                +{tutor.subjects.length - 5} more
              </span>
            )}
          </div>
        </div>

        {/* Educational Background */}
        {tutor.educational_background && (
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
              Education
            </p>
            <p className="text-sm text-slate-700 line-clamp-2">
              {tutor.educational_background}
            </p>
          </div>
        )}

        {/* Bio */}
        {tutor.bio && (
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
              About
            </p>
            <p className="text-sm text-slate-600 line-clamp-2">
              {tutor.bio}
            </p>
          </div>
        )}

        {/* Next Available Slots */}
        <div className="mb-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
            <Calendar className="h-3 w-3 inline mr-1" />
            Next Available
          </p>
          {tutor.next_available_slots?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {tutor.next_available_slots.map((slot, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-lg border border-green-200"
                >
                  {slot.day} {slot.time}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 italic">
              Please chat with admin for scheduling assistance.
            </p>
          )}
        </div>

        {/* Select/Contact Button */}
        <Button 
          onClick={(e) => {
            e.stopPropagation();
            if (isSelected) {
              onContact && onContact(tutor);
            } else {
              onSelect && onSelect(tutor);
            }
          }}
          className={`w-full rounded-full ${
            isSelected 
              ? 'bg-indigo-600 hover:bg-indigo-700' 
              : 'bg-slate-900 hover:bg-slate-800'
          }`}
          data-testid={`select-tutor-${tutor.profile_id || tutor.user_id}`}
        >
          {isSelected ? (
            <>
              <MessageCircle className="h-4 w-4 mr-2" />
              Contact This Tutor
            </>
          ) : (
            <>
              <UserCheck className="h-4 w-4 mr-2" />
              Select This Tutor
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

// Matching Tutors Section
const MatchingTutors = ({ tutors, loading, selectedSubjects, onContact, selectedTutor, onSelectTutor }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700 mb-4" />
        <p className="text-slate-600">Finding matching tutors...</p>
      </div>
    );
  }

  if (tutors.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
          <Users className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="font-heading text-xl text-slate-900 mb-2">No Tutors Found</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          We don't have any tutors available for the selected subjects yet. 
          Please contact us for assistance.
        </p>
        <Link to="/contact">
          <Button variant="outline" className="mt-4 rounded-full">
            Contact Us
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-slate-600">
          Found <span className="font-semibold text-slate-900">{tutors.length}</span> tutor{tutors.length !== 1 ? 's' : ''} for:{' '}
          <span className="text-indigo-700 font-medium">{selectedSubjects.join(', ')}</span>
        </p>
        <p className="text-sm text-indigo-600 font-medium">
          Click on a tutor card to select your preference
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tutors.map((tutor) => {
          const tutorId = tutor.profile_id || tutor.user_id;
          const selectedId = selectedTutor?.profile_id || selectedTutor?.user_id;
          const isSelected = selectedTutor && tutorId && selectedId === tutorId;
          return (
            <TutorCard 
              key={tutorId} 
              tutor={tutor} 
              onContact={onContact}
              isSelected={isSelected}
              onSelect={onSelectTutor}
            />
          );
        })}
      </div>
    </div>
  );
};

// What's included section
const WhatsIncluded = () => {
  const features = [
    { icon: Users, text: "1-on-1 personalized tutoring sessions" },
    { icon: Star, text: "Expert tutors with verified qualifications" },
    { icon: FileText, text: "Lesson notes included after sessions" },
    { icon: Headphones, text: "Dedicated admin support" }
  ];

  // Card animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  // Subtle icon float animation
  const iconFloat = {
    animate: {
      y: [0, -3, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="py-16 px-6 md:px-12 bg-stone-50">
      <div className="max-w-4xl mx-auto">
        <motion.div className="text-center mb-10" {...fadeUp}>
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-900 mb-4">
            What's Included
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-4">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={cardVariants}
              whileHover={{ 
                y: -4, 
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.08)",
                transition: { duration: 0.2 }
              }}
              className="flex items-center gap-4 p-5 bg-white rounded-xl border border-stone-100 cursor-default transition-colors hover:border-indigo-100"
            >
              <motion.div 
                className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0"
                {...iconFloat}
              >
                <feature.icon className="h-6 w-6 text-indigo-700" />
              </motion.div>
              <span className="text-slate-700 font-medium">{feature.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 px-6 md:px-12 bg-white border-t border-stone-100">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-indigo-700" />
            <span className="font-heading text-lg font-semibold text-slate-900">
              Gifted Academy
            </span>
          </div>
          <p className="text-sm text-slate-500">
            © 2024 Gifted Academy Tutors. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Pricing Page
const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [pricingData, setPricingData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [matchingTutors, setMatchingTutors] = useState([]);
  const [loadingTutors, setLoadingTutors] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);

  // Load subjects on mount
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const response = await subjectsAPI.list();
        setSubjects(response.data.subjects || []);
      } catch (error) {
        console.error('Error loading subjects:', error);
      }
    };
    loadSubjects();
  }, []);

  // Handle price change from calculator
  const handlePriceChange = useCallback((data) => {
    setPricingData(data);
  }, []);

  // Handle subject toggle
  const handleSubjectToggle = (subject) => {
    setSelectedSubjects(prev => 
      prev.includes(subject) 
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  // Load matching tutors when subjects change in step 3
  useEffect(() => {
    const loadMatchingTutors = async () => {
      if (currentStep === 3 && selectedSubjects.length > 0) {
        setLoadingTutors(true);
        try {
          const gradeLevel = pricingData?.studentLevel === 'highSchool' ? 'high_school' : 'university';
          const response = await tutorAPI.matchBySubjects(selectedSubjects, gradeLevel);
          setMatchingTutors(response.data.tutors || []);
        } catch (error) {
          console.error('Error loading tutors:', error);
          toast.error('Failed to load tutors');
          setMatchingTutors([]);
        } finally {
          setLoadingTutors(false);
        }
      }
    };
    loadMatchingTutors();
  }, [currentStep, selectedSubjects, pricingData?.studentLevel]);

  // Handle next step
  const handleNext = () => {
    if (currentStep === 1 && !pricingData) {
      toast.error('Please select a package first');
      return;
    }
    if (currentStep === 2 && selectedSubjects.length === 0) {
      toast.error('Please select at least one subject');
      return;
    }
    setCurrentStep(prev => Math.min(3, prev + 1));
  };

  // Handle back
  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  // Handle contact tutor
  const handleContactTutor = (tutor) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to contact tutors');
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    navigate('/contact', { 
      state: { 
        tutorName: tutor.name,
        subjects: selectedSubjects,
        package: pricingData
      }
    });
  };

  // Handle proceed to checkout
  const handleProceedToCheckout = () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to purchase a package');
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    if (!pricingData) {
      toast.error('Please select a package first');
      return;
    }
    navigate('/checkout', {
      state: {
        packageData: pricingData,
        selectedSubjects: selectedSubjects,
        selectedTutor: selectedTutor
      }
    });
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pricing-page">
      <SEO 
        title="Tutoring Packages & Pricing"
        description="Affordable tutoring packages starting at $35/hour for Mathematics, Physics, and Chemistry. High school and university level tutoring with volume discounts. Calculus, Advanced Functions, Linear Algebra, and more."
        keywords="tutoring prices, tutoring packages, affordable math tutor, calculus tutoring cost, physics tutoring rates, chemistry tutor pricing"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-8 px-6 md:px-12 bg-gradient-to-b from-white to-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
              Get Started
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6">
              Find Your Perfect <span className="text-indigo-700">Tutor</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Select your package, choose your subjects, and we'll match you with expert tutors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <StepIndicator currentStep={currentStep} />

          <AnimatePresence mode="wait">
            {/* Step 1: Package Selection */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-indigo-700 text-white text-sm flex items-center justify-center">1</span>
                      Select Your Package
                    </CardTitle>
                    <p className="text-slate-600">
                      Choose your student level and number of tutoring hours
                    </p>
                  </CardHeader>
                  <CardContent>
                    <PricingCalculator 
                      variant="full" 
                      onPriceChange={handlePriceChange}
                    />
                    
                    <div className="mt-8 flex justify-end">
                      <Button 
                        onClick={handleNext}
                        className="rounded-full bg-slate-900 hover:bg-slate-800 px-8"
                        data-testid="next-step-1"
                      >
                        Continue to Subjects <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 2: Subject Selection */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-indigo-700 text-white text-sm flex items-center justify-center">2</span>
                      Choose Your Subjects
                    </CardTitle>
                    <p className="text-slate-600">
                      Select the subjects you need help with (you can choose multiple)
                    </p>
                  </CardHeader>
                  <CardContent>
                    {/* Package Summary */}
                    {pricingData && (
                      <div className="mb-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                        <p className="text-sm text-indigo-700 font-medium">
                          Your Package: {pricingData.packageName} • {pricingData.hours} hours • ${pricingData.hourlyRate}/hr • Total: ${pricingData.totalCost}
                        </p>
                      </div>
                    )}

                    <SubjectSelection 
                      subjects={subjects}
                      selectedSubjects={selectedSubjects}
                      onToggle={handleSubjectToggle}
                      gradeLevel={pricingData?.studentLevel}
                    />
                    
                    <div className="mt-8 flex justify-between">
                      <Button 
                        variant="outline"
                        onClick={handleBack}
                        className="rounded-full px-6"
                        data-testid="back-step-2"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                      </Button>
                      <Button 
                        onClick={handleNext}
                        disabled={selectedSubjects.length === 0}
                        className="rounded-full bg-slate-900 hover:bg-slate-800 px-8"
                        data-testid="next-step-2"
                      >
                        Find Tutors <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 3: Matching Tutors */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-indigo-700 text-white text-sm flex items-center justify-center">3</span>
                      Matching Tutors
                    </CardTitle>
                    <p className="text-slate-600">
                      Here are tutors who teach your selected subjects
                    </p>
                  </CardHeader>
                  <CardContent>
                    {/* Summary */}
                    {pricingData && (
                      <div className="mb-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100 flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <p className="text-sm text-indigo-700 font-medium">
                            {pricingData.packageName} • {pricingData.hours} hours • ${pricingData.totalCost} total
                          </p>
                          <p className="text-xs text-indigo-600 mt-1">
                            Subjects: {selectedSubjects.join(', ')}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setCurrentStep(1)}
                          className="rounded-full text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                        >
                          Modify Selection
                        </Button>
                      </div>
                    )}

                    <MatchingTutors 
                      tutors={matchingTutors}
                      loading={loadingTutors}
                      selectedSubjects={selectedSubjects}
                      onContact={handleContactTutor}
                      selectedTutor={selectedTutor}
                      onSelectTutor={setSelectedTutor}
                    />
                    
                    {/* Checkout Section */}
                    <div className="mt-8 p-6 bg-gradient-to-r from-indigo-700 to-indigo-800 rounded-2xl text-white">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div>
                          <h3 className="font-heading text-xl font-semibold mb-1">
                            Ready to Purchase Your Package?
                          </h3>
                          <p className="text-indigo-200 text-sm">
                            {pricingData.hours} hours at ${pricingData.hourlyRate}/hr = ${pricingData.totalCost} total
                          </p>
                          {selectedTutor && (
                            <p className="text-indigo-100 text-sm mt-1">
                              <UserCheck className="h-3 w-3 inline mr-1" />
                              Preferred tutor: <span className="font-medium">{selectedTutor.name}</span>
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={handleProceedToCheckout}
                          className="rounded-full bg-white text-indigo-700 hover:bg-indigo-50 px-8"
                          data-testid="proceed-to-checkout-btn"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Proceed to Checkout
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mt-8 flex justify-between">
                      <Button 
                        variant="outline"
                        onClick={handleBack}
                        className="rounded-full px-6"
                        data-testid="back-step-3"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Subjects
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <WhatsIncluded />
      <Footer />
    </div>
  );
};

export default PricingPage;
